import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  Image,
  Alert,
  Platform,
  TouchableOpacity,
} from 'react-native';
import ModernButton from '../components/ModernButton';
import ModernCard from '../components/ModernCard';
import { parseBusinessCardText, ParsedBusinessCard } from '../utils/businessCardParser';
import { businessInvitationService } from '../services/businessInvitationService';
import { BUSINESS_CATEGORIES } from '../constants/businessCategories';
import { UnifiedAuthService } from '../services/unifiedAuthService';

interface ScanBusinessCardScreenProps {
  onBack: () => void;
}

interface CardForm {
  businessName: string;
  businessType: string;
  phone: string;
  email: string;
  zipcode: string;
  address: string;
}

const emptyForm: CardForm = {
  businessName: '',
  businessType: BUSINESS_CATEGORIES[0]?.id ?? '',
  phone: '',
  email: '',
  zipcode: '',
  address: '',
};

const ScanBusinessCardScreen: React.FC<ScanBusinessCardScreenProps> = ({ onBack }) => {
  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState<CardForm>(emptyForm);
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);

  const applyParsedCard = (parsed: ParsedBusinessCard) => {
    setForm(prev => ({
      ...prev,
      businessName: parsed.businessName ?? prev.businessName,
      phone: parsed.phone ?? prev.phone,
      email: parsed.email ?? prev.email,
      zipcode: parsed.zipGuess ?? prev.zipcode,
      address: parsed.addressGuess ?? prev.address,
    }));
  };

  const handleScan = async () => {
    if (Platform.OS === 'web') {
      Alert.alert('Not available on web', 'Business card scanning uses the phone camera — open BookAPT on iOS or Android to scan.');
      return;
    }

    setScanning(true);
    try {
      const { launchCamera } = require('react-native-image-picker');
      const result = await launchCamera({
        mediaType: 'photo',
        cameraType: 'back',
        quality: 0.8,
        saveToPhotos: false,
      });

      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Camera error', result.errorMessage || 'Could not open the camera.');
        return;
      }

      const uri = result.assets?.[0]?.uri;
      if (!uri) {
        Alert.alert('No photo', 'Could not read the photo you took. Try again.');
        return;
      }
      setPhotoUri(uri);

      const TextRecognition = require('@react-native-ml-kit/text-recognition').default;
      const ocrResult = await TextRecognition.recognize(uri);
      const parsed = parseBusinessCardText(ocrResult?.text ?? '');

      if (!parsed.businessName && !parsed.phone && !parsed.email) {
        Alert.alert(
          'Could not read the card',
          "We couldn't pick out any details from that photo. You can fill in the fields below by hand."
        );
      }
      applyParsedCard(parsed);
    } catch (error) {
      console.error('Business card scan failed:', error);
      Alert.alert('Scan failed', 'Something went wrong reading the card. Try again or enter the details manually.');
    } finally {
      setScanning(false);
    }
  };

  const updateField = (field: keyof CardForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSendInvite = async () => {
    if (!form.businessName.trim()) {
      Alert.alert('Missing name', 'Enter the business name before sending an invite.');
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      Alert.alert('Missing contact info', 'Add a phone number or email so we can send the invite.');
      return;
    }
    if (!form.zipcode.trim()) {
      Alert.alert('Missing zip code', "Enter the business's zip code.");
      return;
    }

    setSending(true);
    try {
      const currentUser = await UnifiedAuthService.getCurrentUser();
      const result = await businessInvitationService.inviteBusinessDirectly({
        businessName: form.businessName.trim(),
        businessType: form.businessType,
        businessAddress: form.address.trim() || undefined,
        businessPhone: form.phone.trim() || undefined,
        businessEmail: form.email.trim() || undefined,
        zipcode: form.zipcode.trim(),
        invitedBy: currentUser?.id ?? 'sales_rep',
      });

      if (result.success) {
        setLastInviteLink(result.link ?? null);
        Alert.alert('Invite sent', result.message, [
          {
            text: 'Scan another card',
            onPress: () => {
              setForm(emptyForm);
              setPhotoUri(null);
            },
          },
          { text: 'Done', style: 'cancel' },
        ]);
      } else {
        Alert.alert('Could not send invite', result.message);
      }
    } catch (error) {
      console.error('Failed to send business card invite:', error);
      Alert.alert('Error', "Something went wrong sending that invite. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack}>
          <Text style={styles.backLink}>‹ Back</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Scan Business Card</Text>
        <Text style={styles.subtitle}>
          Photograph a business card to pre-fill their info, then send them a link to explore BookAPT.
        </Text>
      </View>

      <ModernCard style={styles.scanCard}>
        {photoUri ? (
          <Image source={{ uri: photoUri }} style={styles.preview} resizeMode="cover" />
        ) : (
          <View style={styles.previewPlaceholder}>
            <Text style={styles.previewPlaceholderText}>No card photographed yet</Text>
          </View>
        )}
        <ModernButton
          title={photoUri ? 'Retake Photo' : 'Take Photo of Card'}
          onPress={handleScan}
          loading={scanning}
          fullWidth
          style={styles.scanButton}
        />
      </ModernCard>

      <ModernCard title="Business Details" style={styles.formCard}>
        <Text style={styles.label}>Business Name</Text>
        <TextInput
          style={styles.input}
          value={form.businessName}
          onChangeText={text => updateField('businessName', text)}
          placeholder="e.g. Bella Nails & Spa"
        />

        <Text style={styles.label}>Category</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryRow}>
          {BUSINESS_CATEGORIES.map(category => (
            <TouchableOpacity
              key={category.id}
              style={[
                styles.categoryChip,
                form.businessType === category.id && styles.categoryChipActive,
              ]}
              onPress={() => updateField('businessType', category.id)}
            >
              <Text
                style={[
                  styles.categoryChipText,
                  form.businessType === category.id && styles.categoryChipTextActive,
                ]}
              >
                {category.icon} {category.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        <Text style={styles.label}>Phone</Text>
        <TextInput
          style={styles.input}
          value={form.phone}
          onChangeText={text => updateField('phone', text)}
          placeholder="(555) 123-4567"
          keyboardType="phone-pad"
        />

        <Text style={styles.label}>Email</Text>
        <TextInput
          style={styles.input}
          value={form.email}
          onChangeText={text => updateField('email', text)}
          placeholder="owner@business.com"
          keyboardType="email-address"
          autoCapitalize="none"
        />

        <Text style={styles.label}>Address</Text>
        <TextInput
          style={styles.input}
          value={form.address}
          onChangeText={text => updateField('address', text)}
          placeholder="123 Main St, Suite 4"
        />

        <Text style={styles.label}>Zip Code</Text>
        <TextInput
          style={styles.input}
          value={form.zipcode}
          onChangeText={text => updateField('zipcode', text)}
          placeholder="78701"
          keyboardType="number-pad"
        />
      </ModernCard>

      {lastInviteLink ? (
        <ModernCard style={styles.linkCard}>
          <Text style={styles.linkLabel}>Last invite link sent</Text>
          <Text style={styles.linkValue}>{lastInviteLink}</Text>
        </ModernCard>
      ) : null}

      <ModernButton
        title="Send Invite Link"
        onPress={handleSendInvite}
        loading={sending}
        fullWidth
        style={styles.sendButton}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  header: {
    marginBottom: 16,
  },
  backLink: {
    color: '#007AFF',
    fontSize: 16,
    marginBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  subtitle: {
    fontSize: 14,
    color: '#6C6C70',
    marginTop: 4,
  },
  scanCard: {
    marginBottom: 16,
    padding: 16,
  },
  preview: {
    width: '100%',
    height: 180,
    borderRadius: 12,
    marginBottom: 12,
    backgroundColor: '#E5E5EA',
  },
  previewPlaceholder: {
    width: '100%',
    height: 180,
    borderRadius: 12,
    marginBottom: 12,
    backgroundColor: '#E5E5EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewPlaceholderText: {
    color: '#8E8E93',
    fontSize: 14,
  },
  scanButton: {
    marginTop: 4,
  },
  formCard: {
    marginBottom: 16,
    padding: 16,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6C6C70',
    marginTop: 12,
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: '#1C1C1E',
  },
  categoryRow: {
    flexDirection: 'row',
  },
  categoryChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    backgroundColor: '#E5E5EA',
    marginRight: 8,
  },
  categoryChipActive: {
    backgroundColor: '#007AFF',
  },
  categoryChipText: {
    fontSize: 13,
    color: '#1C1C1E',
  },
  categoryChipTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  linkCard: {
    marginBottom: 16,
    padding: 16,
  },
  linkLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6C6C70',
    marginBottom: 4,
  },
  linkValue: {
    fontSize: 14,
    color: '#007AFF',
  },
  sendButton: {
    marginTop: 4,
  },
});

export default ScanBusinessCardScreen;
